import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';

import { TranslationModule } from '@core/translation/translation.module';
import { Product } from '@shared/models/product';
import { IUnit } from '@shared/models/unit';
import { ITaxRate } from '@shared/models/tax-rate';
import { BcmService } from '@modules/bcm/bcm.service';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import { formatMoney, grossOf } from '../../utils/booking-v2-pricing';
import { filterBookingV2ProductSuggestions } from '../../utils/booking-v2-product-suggestions.util';

export interface BookingV2ServiceInlineCommit {
    /** Katalogtreffer; `null` = manuelle Freitext-Zeile (productId null in der Buchung). */
    product: Product | null;
    itemNumber: string;
    title: string;
    quantity: number;
    unitId: number | null;
    price: number;
    taxRateId: number | null;
    vatValue: number;
}

@Component({
    selector: 'booking-v2-service-inline-entry',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        Checkbox,
        InputText,
        TranslationModule,
        U2bIconButtonComponent,
    ],
    templateUrl: './booking-v2-service-inline-entry.component.html',
    styleUrl: './booking-v2-service-inline-entry.component.scss',
    host: {
        class: 'booking-v2-service-inline-entry items-start transition-colors',
        '[class.booking-v2-service-inline-entry--warning]': 'showUncommittedWarning()',
        '[style.display]': '"grid"',
        '[style.grid-template-columns]': 'gridTemplate()',
    },
})
export class BookingV2ServiceInlineEntryComponent {

    private readonly host = inject(ElementRef<HTMLElement>);

    readonly products = input.required<Product[]>();
    readonly units = input.required<IUnit[]>();
    readonly taxRates = input.required<ITaxRate[]>();
    readonly gridTemplate = input.required<string>();
    /** Fallback when the row has no product tax (manual lines). */
    readonly fallbackTaxRateId = input<number | null>(null);
    readonly fallbackUnitId = input<number | null>(null);

    readonly committed = output<BookingV2ServiceInlineCommit>();
    readonly openAdvanced = output<void>();

    readonly currentLocale = BcmService.currentLocale;
    readonly fmt = formatMoney;

    readonly itemNumber = signal('');
    readonly titleQuery = signal('');
    readonly quantity = signal(1);
    readonly unitId = signal<number | null>(null);
    readonly price = signal<number | null>(null);
    readonly taxRateId = signal<number | null>(null);
    readonly selectedProduct = signal<Product | null>(null);
    readonly suggestionsOpen = signal(false);
    readonly highlightedSuggestionIndex = signal(-1);
    readonly showUncommittedWarning = signal(false);
    readonly isPriceEditing = signal(false);

    private readonly itemNumberInput = viewChild<ElementRef<HTMLInputElement>>('itemNumberInput');
    private readonly titleInput = viewChild<ElementRef<HTMLTextAreaElement>>('titleInput');
    private pickingSuggestion = false;

    filteredProducts(): Product[] {
        if (this.selectedProduct()) {
            return [];
        }

        return filterBookingV2ProductSuggestions(this.products(), this.itemNumber(), this.titleQuery());
    }

    grossTotal(): number {
        const product = this.selectedProduct();
        const taxRateId = this.resolvedTaxRateId(product);
        const vat = this.vatValueFor(taxRateId, product?.taxRate?.value);
        const unitPrice = this.displayUnitPrice();
        return grossOf(this.quantity(), unitPrice, vat);
    }

    displayUnitPrice(): number {
        return this.price() ?? this.selectedProduct()?.price ?? 0;
    }

    openPriceEdit(): void {
        if (this.isPriceEditing()) {
            return;
        }
        this.isPriceEditing.set(true);
        queueMicrotask(() => {
            const el = this.hostElement.querySelector('input.inline-field--price') as HTMLInputElement | null;
            el?.focus();
            el?.select();
        });
    }

    onPriceBlur(): void {
        this.isPriceEditing.set(false);
    }

    focusEntry(): void {
        this.itemNumberInput()?.nativeElement.focus();
    }

    onItemNumberChange(value: string): void {
        this.itemNumber.set(value);
        this.selectedProduct.set(null);
        this.openSuggestions();
        this.syncHighlightedSuggestion();
    }

    onTitleChange(value: string): void {
        this.titleQuery.set(value);
        this.selectedProduct.set(null);
        this.openSuggestions();
        this.syncHighlightedSuggestion();
    }

    openSuggestions(): void {
        this.suggestionsOpen.set(true);
        this.showUncommittedWarning.set(false);
        this.syncHighlightedSuggestion();
    }

    isSuggestionHighlighted(index: number): boolean {
        return this.highlightedSuggestionIndex() === index;
    }

    onLookupFieldBlur(): void {
        queueMicrotask(() => {
            if (this.pickingSuggestion) {
                return;
            }
            if (!this.hostElement.contains(document.activeElement)) {
                this.suggestionsOpen.set(false);
            }
        });
    }

    onSuggestionMouseDown(product: Product, event: Event): void {
        event.preventDefault();
        this.pickingSuggestion = true;
        this.pickProduct(product, { commitImmediately: true });
        queueMicrotask(() => {
            this.pickingSuggestion = false;
        });
    }

    pickProduct(product: Product, options?: { commitImmediately?: boolean }): void {
        this.selectedProduct.set(product);
        this.itemNumber.set(product.itemNumber || '');
        this.titleQuery.set(product.name);
        this.unitId.set(product.unit?.id ?? this.unitId());
        this.price.set(product.price);
        this.taxRateId.set(product.taxRate?.id ?? this.taxRateId());
        this.suggestionsOpen.set(false);
        this.highlightedSuggestionIndex.set(-1);
        this.showUncommittedWarning.set(false);

        if (options?.commitImmediately) {
            this.tryCommitRow(true);
            return;
        }

        queueMicrotask(() => this.focusQuantity());
    }

    onLookupKeydown(event: Event): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }

        const target = event.target as HTMLElement;
        const isTitleField = target.classList.contains('inline-field--title');
        const isItemNumberField = target.classList.contains('inline-field--item');

        if (event.key === 'Enter' && (isTitleField || isItemNumberField)) {
            if (isTitleField && event.shiftKey) {
                return;
            }

            event.preventDefault();
            this.syncLookupFieldFromTarget(target);
            this.handleLookupEnter();
            return;
        }

        const list = this.filteredProducts();
        const listOpen = this.suggestionsOpen() && list.length > 0;

        if (listOpen && event.key === 'ArrowDown') {
            event.preventDefault();
            const current = this.highlightedSuggestionIndex();
            const next = current < 0 ? 0 : Math.min(current + 1, list.length - 1);
            this.highlightedSuggestionIndex.set(next);
            this.scrollHighlightedSuggestionIntoView();
            return;
        }

        if (listOpen && event.key === 'ArrowUp') {
            event.preventDefault();
            const current = this.highlightedSuggestionIndex();
            const next = current < 0 ? list.length - 1 : Math.max(current - 1, 0);
            this.highlightedSuggestionIndex.set(next);
            this.scrollHighlightedSuggestionIntoView();
            return;
        }

        if (event.key === 'Escape') {
            this.suggestionsOpen.set(false);
            this.highlightedSuggestionIndex.set(-1);
            return;
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            this.onFieldArrowKey(event);
        }
    }

    onFieldArrowKey(event: Event): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }

        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
            return;
        }

        const target = event.target as HTMLElement;
        const fields = this.inlineFocusFields();
        const idx = fields.indexOf(target);
        if (idx === -1) {
            return;
        }

        const next = fields[idx + (event.key === 'ArrowRight' ? 1 : -1)];
        if (next) {
            event.preventDefault();
            next.focus();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.tryCommitRow(true);
        }
    }

    onQuantityEnter(event: Event): void {
        if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        this.tryCommitRow(true);
    }

    onLastFieldTab(event: Event): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }

        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            this.tryCommitRow(true);
        }
    }

    onPriceEnter(event: Event): void {
        if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        this.isPriceEditing.set(false);
        this.tryCommitRow(true);
    }

    onPriceKeydown(event: Event): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            this.onFieldArrowKey(event);
        }
    }

    tryCommitRow(focusNext = false): void {
        const product = this.selectedProduct();
        const title = this.titleQuery().trim();
        const itemNumber = this.itemNumber().trim();

        if (!product && !title && !itemNumber) {
            return;
        }

        if (product) {
            const taxRateId = this.resolvedTaxRateId(product);
            this.emitCommit({
                product,
                itemNumber: itemNumber || product.itemNumber || '',
                title: title || product.name,
                quantity: this.quantity() || 1,
                unitId: this.unitId() ?? product.unit?.id ?? this.fallbackUnitId(),
                price: this.price() ?? product.price,
                taxRateId,
                vatValue: this.vatValueFor(taxRateId, product.taxRate?.value),
            }, focusNext);
            return;
        }

        if (!title) {
            this.showUncommittedWarning.set(true);
            return;
        }

        const unitPrice = this.price();
        if (unitPrice == null || !Number.isFinite(unitPrice) || unitPrice <= 0) {
            this.showUncommittedWarning.set(true);
            return;
        }

        const taxRateId = this.resolvedTaxRateId(null);
        if (taxRateId == null) {
            this.showUncommittedWarning.set(true);
            return;
        }

        this.emitCommit({
            product: null,
            itemNumber,
            title,
            quantity: this.quantity() || 1,
            unitId: this.unitId() ?? this.fallbackUnitId(),
            price: unitPrice,
            taxRateId,
            vatValue: this.vatValueFor(taxRateId, null),
        }, focusNext);
    }

    onPriceTriggerKeydown(event: Event): void {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            this.tryCommitRow(true);
            return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            this.onFieldArrowKey(event);
        }
    }

    private emitCommit(payload: BookingV2ServiceInlineCommit, focusNext: boolean): void {
        this.committed.emit(payload);
        this.resetRow();
        if (focusNext) {
            queueMicrotask(() => this.focusEntry());
        }
    }

    private resolvedTaxRateId(product: Product | null): number | null {
        return this.taxRateId()
            ?? product?.taxRate?.id
            ?? this.fallbackTaxRateId();
    }

    private vatValueFor(taxRateId: number | null, productFallback: number | null | undefined): number {
        const fromId = this.taxRates().find(t => t.id === taxRateId)?.value;
        if (fromId != null) {
            return fromId;
        }
        return productFallback ?? 0;
    }

    resetRow(): void {
        this.itemNumber.set('');
        this.titleQuery.set('');
        this.quantity.set(1);
        this.unitId.set(null);
        this.price.set(null);
        this.taxRateId.set(null);
        this.selectedProduct.set(null);
        this.suggestionsOpen.set(false);
        this.highlightedSuggestionIndex.set(-1);
        this.showUncommittedWarning.set(false);
        this.isPriceEditing.set(false);
    }

    hasDraft(): boolean {
        return !!(this.selectedProduct() || this.itemNumber().trim() || this.titleQuery().trim());
    }

    private focusQuantity(): void {
        const input = this.hostElement.querySelector('input.inline-field--qty') as HTMLInputElement | null;
        input?.focus();
    }

    private get hostElement(): HTMLElement {
        return this.host.nativeElement as HTMLElement;
    }

    private inlineFocusFields(): HTMLElement[] {
        return Array.from(
            this.hostElement.querySelectorAll(
                'input.inline-field, textarea.inline-field, select.inline-field, button.inline-price-trigger',
            ),
        ) as HTMLElement[];
    }

    private syncHighlightedSuggestion(): void {
        this.highlightedSuggestionIndex.set(this.filteredProducts().length > 0 ? 0 : -1);
    }

    private handleLookupEnter(): void {
        const selected = this.selectedProduct();
        if (selected) {
            this.tryCommitRow(true);
            return;
        }

        const list = this.filteredProducts();
        if (this.suggestionsOpen() && list.length) {
            const index = this.highlightedSuggestionIndex();
            const product = list[index >= 0 ? index : 0];
            if (product) {
                this.pickProduct(product, { commitImmediately: true });
            }
            return;
        }

        const itemRaw = this.itemNumber().trim();
        if (itemRaw) {
            const byItemNumber = this.products().find(
                p => (p.itemNumber || '').toLowerCase() === itemRaw.toLowerCase(),
            );
            if (byItemNumber) {
                this.pickProduct(byItemNumber, { commitImmediately: true });
                return;
            }
        }

        const titleRaw = this.titleQuery().trim();
        if (titleRaw) {
            const byExactName = this.products().find(
                p => p.name.toLowerCase() === titleRaw.toLowerCase(),
            );
            if (byExactName) {
                this.pickProduct(byExactName, { commitImmediately: true });
                return;
            }
        }

        if (list.length === 1) {
            this.pickProduct(list[0], { commitImmediately: true });
        }
    }

    private syncLookupFieldFromTarget(target: HTMLElement): void {
        if (target.classList.contains('inline-field--item')) {
            this.itemNumber.set((target as HTMLInputElement).value);
            return;
        }

        if (target.classList.contains('inline-field--title')) {
            this.titleQuery.set((target as HTMLTextAreaElement).value);
        }
    }

    private scrollHighlightedSuggestionIntoView(): void {
        queueMicrotask(() => {
            const index = this.highlightedSuggestionIndex();
            if (index < 0) {
                return;
            }

            const option = this.hostElement.querySelector(
                `[data-suggestion-index="${index}"]`,
            ) as HTMLElement | null;
            option?.scrollIntoView({ block: 'nearest' });
        });
    }
}
